/* import __COLOCATED_TEMPLATE__ from './conversation-ingestion-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type Store from '@ember-data/store';
// @ts-ignore
import { copy } from 'ember-copy';
import type Attribute from 'embercom/models/attribute';
import { type default as PredicateGroup } from 'predicates/models/predicate-group';
import { type default as Predicate } from 'predicates/models/predicate';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type CopilotSettings from 'embercom/models/copilot-settings';

interface Args {
  onClose: () => void;
  isOpen: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class ConversationIngestionSideDrawer extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;
  @service declare knowledgeHubService: KnowledgeHubService;

  @tracked openSectionId: string | null = 'sync-conversation-history';
  @tracked isSyncEnabled = false;
  @tracked predicateGroup: PredicateGroup;
  @tracked selectedSyncOption = 'sync-all';
  currentSyncOption = 'sync-all';
  @tracked copilotSettings: CopilotSettings | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    // Fetch import source
    taskFor(this.loadImportSource).perform();
    this.isSyncEnabled = this.historicalSyncingEnabled;

    // Initialize predicateGroup from the source or create an empty state
    let existingPredicateGroup =
      this.finStandaloneService.standaloneImportSource?.context?.predicate_group;
    if (existingPredicateGroup) {
      this.predicateGroup = existingPredicateGroup;
    } else {
      this.predicateGroup = this.store.createFragment('predicates/predicate-group');
    }

    // if predicates exist, we set the sync option to filtered
    let existingPredicates = this.predicateGroup?.predicates;
    this.currentSyncOption =
      existingPredicates && existingPredicates.length > 0 ? 'filtered' : 'sync-all';
    this.selectedSyncOption = this.currentSyncOption;

    // Fetch copilot settings
    taskFor(this.fetchCopilotSettings).perform();
  }

  @dropTask
  *loadImportSource() {
    try {
      yield this.finStandaloneService.fetchStandaloneImportSource();
    } catch (e) {
      console.error('Failed to fetch import source settings:', e);
    }
  }

  @dropTask
  *fetchCopilotSettings() {
    try {
      let settings: CopilotSettings = yield this.store.findRecord(
        'copilot-settings',
        this.appService.app.id,
      );
      this.copilotSettings = settings;
    } catch (e) {
      console.error('Failed to fetch copilot settings:', e);
      this.copilotSettings = null;
    }
  }

  get ticketFields(): Attribute[] {
    return this.finStandaloneService.standaloneImportSource?.metadata ?? [];
  }

  get historicalSyncingEnabled() {
    return this.appService.mainStandalonePlatform.historicalSyncingEnabled;
  }

  get headerTitle(): string {
    return (
      this.appService.mainStandalonePlatform.componentData['conversation-ingestion']?.drawerTitle ??
      this.intl.t('standalone.common.conversation-ingestion.conversation-history')
    );
  }

  get hasCopilotSettingsChanged() {
    return this.copilotSettings?.hasDirtyAttributes ?? false;
  }

  get hasPredicatesChanged() {
    return this.finStandaloneService.standaloneImportSource?.context?.predicate_group
      .hasDirtyAttributes;
  }

  get validSyncOption() {
    return (
      this.selectedSyncOption === 'sync-all' ||
      (this.selectedSyncOption === 'filtered' &&
        this.predicateGroup?.predicates.length > 0 &&
        this.predicateGroup.isValid)
    );
  }

  get isIntegrationConnected() {
    return this.appService.mainStandalonePlatform.isConnected;
  }

  get shouldAllowChanges() {
    return (
      this.isIntegrationConnected &&
      (this.hasConversationSyncChanged || this.hasCopilotSettingsChanged) &&
      this.validSyncOption
    );
  }

  get hasConversationSyncChanged() {
    return (
      this.historicalSyncingEnabled !== this.isSyncEnabled ||
      this.currentSyncOption !== this.selectedSyncOption ||
      (this.selectedSyncOption === 'filtered' && this.hasPredicatesChanged)
    );
  }

  get canSeeCopilotSettings() {
    return this.appService.app.canUseConversationExcerptsOnStandalone;
  }

  @action
  updateSyncToggle(): void {
    this.isSyncEnabled = !this.isSyncEnabled;
  }

  @action
  onUpdatePredicates(predicates: Predicate[]): void {
    this.predicateGroup?.set('predicates', copy(predicates));
  }

  @action
  onSyncOptionChange(value: string): void {
    this.selectedSyncOption = value;
  }

  @action
  async updateConversationSyncing() {
    let updateSyncHistory = this.historicalSyncingEnabled !== this.isSyncEnabled;
    if (updateSyncHistory) {
      // If we're disabling ask for confirmation
      if (this.finStandaloneService.isConversationSyncingEnabled) {
        let isConfirmed = await this.intercomConfirmService.confirm({
          body:
            this.appService.mainStandalonePlatform.componentData['conversation-ingestion']
              ?.disableSyncBody ?? '',
          confirmButtonText: this.intl.t('standalone.conv-syncing.disable-sync-continue'),
          title: this.intl.t('standalone.conv-syncing.disable-sync-title'),
        });

        if (!isConfirmed) {
          return;
        }

        this.finStandaloneService.isConversationSyncingEnabled = false;
      } else {
        this.finStandaloneService.isConversationSyncingEnabled = true;
      }
    }
    if (updateSyncHistory || this.hasConversationSyncChanged) {
      await this.updateConversationSyncingState(updateSyncHistory);
    }
    await this.updateCopilotSettings();
  }

  get importingEnabledNotificationMessage() {
    return (
      this.appService.mainStandalonePlatform.componentData['conversation-ingestion']
        ?.importingEnabledNotificationMessage ?? ''
    );
  }

  private async updateConversationSyncingState(updateSyncHistory: boolean) {
    if (!this.appService.mainStandalonePlatform.isConnected) {
      return;
    }
    try {
      // Clean predicates if sync-all is selected
      if (this.selectedSyncOption === 'sync-all') {
        this.predicateGroup?.set('predicates', []);
      }

      await this.finStandaloneService.updateConversationSyncing(
        updateSyncHistory,
        this.predicateGroup,
        this.selectedSyncOption,
      );
      await this.finStandaloneService.fetchStandaloneImportSource();
      if (this.finStandaloneService.isConversationSyncingEnabled) {
        this.notificationsService.notifyConfirmation(this.importingEnabledNotificationMessage);
      }
      this.currentSyncOption = this.selectedSyncOption;
    } catch (e) {
      this.finStandaloneService.isConversationSyncingEnabled =
        !this.finStandaloneService.isConversationSyncingEnabled;
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.notifications.toggle-conversation-sync-failure-notify'),
        },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
      console.error(e.message || e.jqXHR);
    }
  }

  private async updateCopilotSettings() {
    if (!this.hasCopilotSettingsChanged) {
      return;
    }

    try {
      await this.knowledgeHubService.ensurePermissions();
    } catch (e) {
      return;
    }

    try {
      if (!this.copilotSettings) {
        throw new Error('Copilot settings not loaded');
      }

      await this.copilotSettings.save();

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'standalone.common.conversation-ingestion.conversation-history-for-copilot.success-notification',
        ),
      );
      this.args.onClose();
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t(
          'standalone.common.conversation-ingestion.conversation-history-for-copilot.error-notification',
        ),
      );
      throw new Error('Failed to save copilot settings', e.message);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Common::ConversationIngestion::ConversationIngestionSideDrawer': typeof ConversationIngestionSideDrawer;
  }
}
